import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Container,
  styled,
} from '@mui/material';
import { getAllProposals, getProposalByLenderAndId } from '../../services/proposalService';
import ProposalList from '../../components/common/ProposalList/ProposalList';
import ProposalHeader from '../../components/common/ProposalList/ProposalHeader';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import PreviewApplication from '../../pages/apply-loan/PreviewApplication';

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "60vh",
});

const formatProposal = (proposalData) => ({
  id: proposalData.id,
  uuid: proposalData.uuid,
  proposalId: `PROP-${proposalData.proposal.id}`,
  status: proposalData.status || proposalData.proposal.status || "new",
  createDatetime: proposalData.createDatetime,
  createOpName: proposalData.createOpName,
  borrowerId: proposalData.proposal.borrowerId,
  userId: proposalData.proposal.userId,
  lender: proposalData.lender,
  proposal: proposalData.proposal,
  applicationId: proposalData.proposal?.applicationForm?.applicationId || "N/A",
  cma: proposalData.proposal.cma,
  onePager: proposalData.proposal.onePager,
  submittedDate: new Date(proposalData.createDatetime).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }),
  applicationForm: proposalData.proposal.applicationForm
});

const Proposals = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
 const userPermission = useSelector((state) => state.user.userPermissions);
  // const [permission, setPermissions] = useState({});
  useEffect(() => {
    console.log("User Permissions:", userPermission);
    const moduleList = userPermission?.map((e) => e?.module);
    if (moduleList && moduleList.length > 0) {
      const hasLeadsPermission = moduleList.includes("proposals");
      if (!hasLeadsPermission) {
        //   showToast("You do not have permission to access Leads", "error");
        navigate("/dashboard");
      }
    }
  }, [userPermission]);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const status = activeTab === 1 ? 'rejected' : null;
      const response = await getAllProposals(status);

      if (response?.statusCode === 200) {
        const proposalsArray = response?.result?.result || [];
        const formattedProposals = proposalsArray.map(formatProposal);
        setProposals(formattedProposals);
      } else {
        setProposals([]);
      }
    } catch (error) {
      toast.error(error.message);
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const handleViewDetails = useCallback(async (proposal) => {
    try {
      const lenderId = proposal.lender?.lenderId;
      const proposalId = proposal.proposal?.id;

      if (!lenderId || !proposalId) {
        toast.error('Missing lenderId or proposalId');
        return;
      }

      const response = await getProposalByLenderAndId(lenderId, proposalId);
      if (response?.statusCode === 200) {
        navigate(`/proposals/details/${proposal.uuid}`, {
          state: {
            proposalData: proposal,
            applicationFormData: proposal.proposal?.applicationForm,
            otherOffers: response?.result?.otherOffers
          },
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, [navigate]);

  const handleViewApplication = useCallback((proposal) => {
    const appForm = proposal?.applicationForm;
    if (!appForm) return;
    const userInfo = {
      Name: appForm?.userId?.name || '',
      Email: appForm?.userId?.email || '',
      MobileNumber: appForm?.userId?.mobileNumber || ''
    };
    const combined = { ...appForm, loanDoc: appForm?.loanDoc || {}, userInfo };
    setPreviewData(combined);
    setPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewData(null);
  }, []);

  const filteredProposals = useMemo(() => {
    if (!Array.isArray(proposals)) return [];

    const searchTerm = searchQuery.toLowerCase();
    return proposals.filter(proposal =>
      proposal.proposalId?.toLowerCase().includes(searchTerm) ||
      proposal.createOpName?.toLowerCase().includes(searchTerm) ||
      proposal.status?.toLowerCase().includes(searchTerm)
    );
  }, [proposals, searchQuery]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <Container maxWidth="xl">
      <ProposalHeader
        title="Proposals"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      
      <ProposalList
        proposals={filteredProposals}
        onViewDetails={handleViewDetails}
        onViewApplication={handleViewApplication}
        groupedByMonth={true}
      />

      <PreviewApplication
        open={previewOpen}
        onClose={handleClosePreview}
        data={previewData}
        readOnly={true}
      />
    </Container>
  );
};

export default React.memo(Proposals); 